import { Column, Row } from 'src/shared/components/Semantic/all'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import { useLocalization } from 'src/shared/hooks/useLocalization'

import {
  PREVIOUS_WORK_PROJECTS,
  bilingual,
  getProjectTestimonial,
} from '../consts'

type Project = (typeof PREVIOUS_WORK_PROJECTS)[number]

/**
 * One project in full: the images, what we made in our own words, and then
 * the customer's own words when we have them.
 */
export const ProjectDialog = ({
  project,
  open,
  onClose,
}: {
  /** Kept after closing, so the content stays through the close animation. */
  project: Project | null
  open: boolean
  onClose: () => void
}) => {
  const { t } = useLocalization()
  const testimonial = project ? getProjectTestimonial(project.id) : undefined

  return (
    <Dialog
      open={open && !!project}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent className="max-h-[90dvh] w-[calc(100%-2rem)] max-w-3xl gap-0 overflow-y-auto p-0">
        {project ? (
          <>
            <div className="relative aspect-[4/3] w-full shrink-0 bg-muted/40 sm:aspect-[2/1]">
              <Row className="absolute inset-0 items-center justify-center gap-4 p-6 md:p-8">
                {project.images.map((src) => (
                  <img
                    key={src}
                    src={src}
                    alt={`${project.customer}, ${t(
                      project.product.en,
                      project.product.sv
                    )}`}
                    className="h-full min-w-0 flex-1 object-contain"
                  />
                ))}
              </Row>
            </div>

            <Column className="gap-6 p-6 md:p-8">
              <Column className="gap-1">
                <DialogTitle className="text-2xl tracking-tight text-slate-900">
                  {project.customer}
                </DialogTitle>
                <p className="text-md text-slate-700">
                  {t(project.product.en, project.product.sv)}
                </p>
                {project.decoration ? (
                  <p className="text-md text-slate-500">
                    {t(project.decoration.en, project.decoration.sv)}
                  </p>
                ) : null}
              </Column>

              <p className="text-lg leading-relaxed text-slate-700">
                {t(project.description.en, project.description.sv)}
              </p>

              {testimonial ? (
                <figure className="m-0 border-t border-slate-200 pt-6">
                  <blockquote className="m-0">
                    <p className="text-lg leading-relaxed text-slate-700">
                      “{t(...bilingual(testimonial.quote))}”
                    </p>
                  </blockquote>
                  <figcaption>
                    <Row className="mt-4 items-center gap-3.5">
                      {testimonial.image ? (
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="h-12 w-12 shrink-0 rounded-md object-cover ring-1 ring-stone-300/70 ring-offset-2"
                        />
                      ) : null}
                      <Column className="min-w-0 items-start gap-0.5 text-left">
                        <span className="text-md font-normal">
                          {testimonial.name}
                        </span>
                        <span className="text-md text-slate-500">
                          {t(...bilingual(testimonial.title))}
                        </span>
                      </Column>
                    </Row>
                  </figcaption>
                </figure>
              ) : null}
            </Column>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
